/**
 * Component: DashboardShiftRow
 * @description Represents a row in the employee's weekly schedule, displaying date, hours, additional shift information, and optional click functionality.
 * For admin users, it also displays labor cost percentages from daily tickets.
 *
 * @props
 *   @param {string} day - The day name (e.g., "Mon", "Tue").
 *   @param {number} dateNumber - The numerical date of the month.
 *   @param {boolean} isToday - Indicates if the row represents the current day.
 *   @param {boolean} isPast - Indicates if the row represents a past date.
 *   @param {string} scheduledHours - The total scheduled hours for the day.
 *   @param {Array} shifts - Array of shifts for the day.
 *   @param {number} futureShiftsCount - The number of future shifts scheduled for the day.
 *   @param {boolean} isLast - Indicates if the row is the last in the list.
 *   @param {Object} dailyTicket - The daily ticket object containing costPercentage and projectedCostPercentage.
 *   @param {function} onDayClick - Callback function that opens a drawer with detailed shift information for the selected day.
 *                                The drawer shows the DailyShiftSummaryPanel component which displays:
 *                                - For admin users: labor cost analysis, net sales, and projected values
 *                                - For regular users: shift details and status
 *
 * @methods
 *   @function getComprehensiveAdditionalText - Calculates additional text to display for the shift, such as status or hours logged.
 *   @function getAdditionalTextColor - Determines the text color for additional information based on shift statuses.
 *
 * @render
 *   - Displays the date, scheduled hours, and additional shift information.
 *   - Shows an arrow icon if the row is clickable.
 *   - Includes a divider to separate rows visually.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  ShiftRowContainer,
  ShiftRowContent,
  ShiftInfoContainer,
  ShiftInfo,
  DayHeadline,
  DateBox,
  DateText,
  DaySubtitle,
  StyledArrowIcon,
} from './DashboardShiftRow.styles';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { getDailyShiftsStatus, getDailyCOLStatus } from '../../utils/shiftUtils';
import { useSelector } from 'react-redux';
import { selectHasPrivilegedRole } from '../../redux/slices/currentUserSlice';
import theme from '../../theme';

const DashboardShiftRow = ({
  day,
  dateNumber,
  isToday,
  isPast,
  scheduledHours,
  shifts = [],
  futureShiftsCount,
  onDayClick,
  isLast,
  dailyTicket,
}) => {
  const hasPrivilegedRole = useSelector(selectHasPrivilegedRole);

  const { message: shiftSubtitle, color: subtitleColor } = hasPrivilegedRole
    ? getDailyCOLStatus(isToday, isPast, dailyTicket)
    : getDailyShiftsStatus({
        isToday,
        isPast,
        dayShifts: shifts,
        futureShiftsCount,
      });

  return (
    <ShiftRowContainer onClick={onDayClick} isLast={isLast}>
      <ShiftRowContent>
        <DateBox className={isToday ? 'is-today' : ''}>
          <DateText variant="body2" className={isToday ? 'is-today' : isPast ? 'is-past' : ''}>
            {day}
          </DateText>
          <DateText variant="h6" className={isToday ? 'is-today' : isPast ? 'is-past' : ''}>
            {dateNumber}
          </DateText>
        </DateBox>
        <ShiftInfoContainer isLast={isLast}>
          <ShiftInfo>
            <DayHeadline variant="bodyRegular">
              {/* Employees see their own scheduled hours, owners and managers see the total number of shifts*/}
              {hasPrivilegedRole ? `${shifts.length} shifts` : scheduledHours}
            </DayHeadline>
            <DaySubtitle variant="body2" color={subtitleColor || theme.colors.gray}>
              {/* Employees see information about their own shifts, owners and managers see the labor cost for that day*/}
              {shiftSubtitle}
            </DaySubtitle>
          </ShiftInfo>
          <StyledArrowIcon as={ArrowForwardIosIcon} />
        </ShiftInfoContainer>
      </ShiftRowContent>
    </ShiftRowContainer>
  );
};

DashboardShiftRow.propTypes = {
  day: PropTypes.string.isRequired,
  dateNumber: PropTypes.number.isRequired,
  isToday: PropTypes.bool.isRequired,
  isPast: PropTypes.bool.isRequired,
  scheduledHours: PropTypes.string.isRequired,
  shifts: PropTypes.array,
  futureShiftsCount: PropTypes.number,
  onDayClick: PropTypes.func,
  isLast: PropTypes.bool,
  dailyTicket: PropTypes.shape({
    costPercentage: PropTypes.number,
    projectedCostPercentage: PropTypes.number,
  }),
};

export default DashboardShiftRow;
